import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowRight, FiChevronDown, FiChevronLeft, FiChevronRight, FiFileText } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { YearPicker } from '../../components/dashboard/DateFilter/YearPicker';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import styles from './CashFlow.module.css';
import { SeletorDePeriodo } from './SeletorDePeriodo';
import { usePreferences } from '../../contexts';
import {
    linhasDoFluxo,
    mesesVisiveis,
    percentualDaReceita,
    soma,
    variacaoPercentual,
    type FluxoData,
    type LinhaDoFluxo,
    type PeriodoExibido,
} from './fluxoTabela';

/** Interruptor dos controles da tabela (Comparar, Meses zerados, Analise horizontal). */
function Interruptor({ ligado, onChange, rotulo }: { ligado: boolean; onChange: (v: boolean) => void; rotulo: string }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={ligado}
            className={styles.toggle}
            onClick={() => onChange(!ligado)}
        >
            <span className={`${styles.toggleTrack} ${ligado ? styles.toggleTrackOn : ''}`}>
                <span className={styles.toggleThumb} />
            </span>
            <span className={styles.toggleLabel}>{rotulo}</span>
        </button>
    );
}

export const CashFlow = () => {
    const { filialAtiva } = useAuth();
    const now = new Date();
    const currentYear = now.getFullYear();

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);

    const [fluxoData, setFluxoData] = useState<FluxoData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [nomeModal, setNomeModal] = useState<string | null>(null);
    const { formatCurrency, t, prefs } = usePreferences();
    const [isMobile, setIsMobile] = useState(false);
    const [mobileMonthIndex, setMobileMonthIndex] = useState(() => new Date().getMonth());

    // Controles da tabela (MAN-134)
    const [periodo, setPeriodo] = useState<PeriodoExibido>('ano');
    const [compararMesAnterior, setCompararMesAnterior] = useState(false);
    const [mostrarZerados, setMostrarZerados] = useState(true);
    const [analiseHorizontal, setAnaliseHorizontal] = useState(false);

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const stickyColRef = useRef<HTMLTableCellElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [stickyColWidth, setStickyColWidth] = useState(220);

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 768px)');
        setIsMobile(mql.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    // Detecta se a tabela realmente estoura a largura disponível, para mostrar
    // a dica de rolagem horizontal só quando ela é de fato necessária, e mede a
    // largura real da coluna fixa (varia com o conteúdo, table-layout: auto não é fixo em 220px)
    // para alinhar a sombra de borda exatamente onde a coluna fixa termina.
    useEffect(() => {
        const el = tableWrapperRef.current;
        if (!el) return;

        const updateScrollState = () => {
            setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
            setCanScrollLeft(el.scrollLeft > 4);
            if (stickyColRef.current) setStickyColWidth(stickyColRef.current.offsetWidth);
        };

        updateScrollState();
        el.addEventListener('scroll', updateScrollState, { passive: true });

        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);
        if (stickyColRef.current) resizeObserver.observe(stickyColRef.current);

        return () => {
            el.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
        };
    }, [fluxoData, isMobile, periodo, compararMesAnterior, mostrarZerados, analiseHorizontal]);

    const translateMonth = (mesEmPortugues: string): string => {
        if (prefs.language === 'pt-BR') return mesEmPortugues;

        const mesesPtBR = [
            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
        ];
        const idx = mesesPtBR.findIndex(
            m => m.toLowerCase() === mesEmPortugues.toLowerCase()
        );
        if (idx === -1) return mesEmPortugues;

        const date = new Date(Date.UTC(2000, idx, 1));
        const label = date.toLocaleString(prefs.language, { month: 'long', timeZone: 'UTC' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    // Carrega os anos com lançamentos para popular o seletor de ano
    useEffect(() => {
        api.getLancamentosAnos().then(anos => {
            const minYear = anos.length > 0 ? Math.min(...anos) : currentYear;
            const years: number[] = [];
            for (let y = minYear; y <= currentYear; y++) years.push(y);
            setAvailableYears(years);
        }).catch(() => {
            setAvailableYears([currentYear]);
        });
    }, [filialAtiva, currentYear]);

    const MAX_NOME = 40;
    const renderNome = (texto: string) => {
        if (texto.length <= MAX_NOME) return <>{texto}</>;
        return (
            <>
                {texto.slice(0, MAX_NOME)}…{' '}
                <button
                    className={styles.verMaisBtn}
                    onClick={(e) => { e.stopPropagation(); setNomeModal(texto); }}
                >
                    {t('common.verMais')}
                </button>
            </>
        );
    };

    const fetchFluxo = useCallback(async () => {
        setIsLoading(true);
        try {
            const dataInicio = `${selectedYear}-01-01`;
            const dataFim = `${selectedYear}-12-31`;
            const data = await api.getCashFlowFluxo(dataInicio, dataFim);
            setFluxoData(data);
        } catch (error) {
            console.error('Erro ao carregar Fluxo de Caixa:', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedYear, filialAtiva]);

    useEffect(() => {
        fetchFluxo();
    }, [fetchFluxo]);

    const toggleCategory = (key: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Estrutura sempre fixa: 12 meses (Janeiro a Dezembro) do ano selecionado
    const meses = fluxoData?.meses ?? [];

    const colunasDosMeses = useMemo(
        () => (fluxoData ? mesesVisiveis(fluxoData, { periodo, mostrarZerados }) : []),
        [fluxoData, periodo, mostrarZerados],
    );
    // No celular cabe um mes por vez: o do periodo escolhido ou o da navegacao.
    const visibleMonthIndexes = isMobile && periodo === 'ano' && meses.length > 0
        ? [Math.min(mobileMonthIndex, meses.length - 1)]
        : colunasDosMeses;
    const mostrarTotal = periodo === 'ano';
    const mesesOcultos = periodo === 'ano' && !mostrarZerados ? meses.length - colunasDosMeses.length : 0;

    const rotulos = { entradas: t('plano.entradas'), saidas: t('plano.saidas'), saldo: t('common.saldo') };
    const linhas = fluxoData ? linhasDoFluxo(fluxoData, rotulos, (k) => expandedCategories.has(k)) : [];

    const entradasDoMes = (i: number) => fluxoData?.entradas.total[i] ?? 0;
    const entradasNoAno = soma(fluxoData?.entradas.total ?? []);

    const formatoPercentual = useMemo(
        () => new Intl.NumberFormat(prefs.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        [prefs.language],
    );
    const formatarPercentual = (p: number | null) => (p === null ? '–' : `${formatoPercentual.format(p)}%`);
    const formatarVariacao = (v: number | null) =>
        v === null ? '–' : `${v >= 0 ? '▲' : '▼'} ${formatoPercentual.format(Math.abs(v))}%`;

    const variacaoDaLinha = (linha: LinhaDoFluxo, i: number) =>
        variacaoPercentual(linha.valores[i] ?? 0, i > 0 ? linha.valores[i - 1] ?? 0 : undefined);

    const corDoSaldo = (v: number) => ({ color: v >= 0 ? '#16a34a' : '#dc2626' });

    const classeDaLinha = (linha: LinhaDoFluxo) => {
        if (linha.nivel === 'grupo') {
            return `${styles.dreGroupHeader} ${linha.lado === 'entrada' ? styles.dreGroupHeaderEntradas : styles.dreGroupHeaderSaidas}`;
        }
        const lado = linha.lado === 'entrada' ? styles.ladoEntrada : styles.ladoSaida;
        if (linha.nivel === 'categoria') return `${styles.dreCategoryRow} ${lado}`;
        if (linha.nivel === 'subcategoria') return `${styles.dreSubRow} ${lado}`;
        return styles.dreResultRow;
    };

    const celulaDeValor = (linha: LinhaDoFluxo, valor: number, entradas: number, key: string | number, total = false) => {
        const negrito = linha.nivel === 'grupo' || linha.nivel === 'saldo';
        const conteudo = formatCurrency(valor);
        // A cor vai no texto: a celula de total tem cor fixa no CSS.
        const cor = linha.nivel === 'saldo' ? corDoSaldo(valor) : linha.nivel === 'grupo' ? { color: '#fff' } : undefined;
        return (
            <td
                key={key}
                className={`${styles.dreValueCell} ${total ? styles.dreTotalCell : ''} ${valor === 0 && !negrito ? styles.valorZerado : ''}`}
            >
                {negrito ? <strong style={cor}>{conteudo}</strong> : conteudo}
                {analiseHorizontal && (
                    <span className={styles.percentual}>{formatarPercentual(percentualDaReceita(valor, entradas))}</span>
                )}
            </td>
        );
    };

    const celulaDeVariacao = (linha: LinhaDoFluxo, i: number) => {
        const v = variacaoDaLinha(linha, i);
        return (
            <td
                key={`d${i}`}
                className={`${styles.dreValueCell} ${styles.deltaCell} ${v === null ? '' : v >= 0 ? styles.deltaUp : styles.deltaDown}`}
            >
                {formatarVariacao(v)}
            </td>
        );
    };

    // ---------------- Exportar (mesmas linhas, colunas e analises da tela, tudo expandido) ----------------

    const nomeDoPeriodo = periodo === 'ano' ? t('cashflow.anoInteiro') : translateMonth(meses[periodo] ?? '');
    const nomeDoArquivo = `fluxo-de-caixa_${selectedYear}${periodo === 'ano' ? '' : `_${String(periodo + 1).padStart(2, '0')}`}`;
    const linhasParaExportar = () => (fluxoData ? linhasDoFluxo(fluxoData, rotulos, () => true) : []);
    const recuo = (linha: LinhaDoFluxo) => (linha.nivel === 'subcategoria' ? '    ' : linha.nivel === 'categoria' ? '  ' : '');

    const handleExportExcel = () => {
        const rows = linhasParaExportar().map((linha) => {
            const row: Record<string, string | number> = { [t('common.descricao')]: recuo(linha) + linha.nome };
            for (const i of colunasDosMeses) {
                const mes = translateMonth(meses[i]);
                row[mes] = linha.valores[i] ?? 0;
                if (analiseHorizontal) {
                    const p = percentualDaReceita(linha.valores[i] ?? 0, entradasDoMes(i));
                    row[`${mes} %`] = p === null ? '–' : Number(p.toFixed(1));
                }
                if (compararMesAnterior) {
                    const v = variacaoDaLinha(linha, i);
                    row[`${mes} Δ%`] = v === null ? '–' : Number(v.toFixed(1));
                }
            }
            if (mostrarTotal) {
                const total = soma(linha.valores);
                row.Total = total;
                if (analiseHorizontal) {
                    const p = percentualDaReceita(total, entradasNoAno);
                    row['Total %'] = p === null ? '–' : Number(p.toFixed(1));
                }
            }
            return row;
        });
        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet['!cols'] = Object.keys(rows[0] ?? {}).map((k, idx) => ({ wch: idx === 0 ? 32 : Math.max(12, k.length + 2) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Fluxo de Caixa');
        XLSX.writeFile(workbook, `${nomeDoArquivo}.xlsx`);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.setFillColor(1, 95, 89);
        doc.rect(0, 0, pageWidth, 26, 'F');
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.text(t('cashflow.titulo'), 14, 13);
        doc.setFontSize(10);
        doc.text(`${selectedYear} · ${nomeDoPeriodo}`, 14, 21);

        // A fonte padrao do PDF nao tem Δ nem ▲/▼: no PDF a variacao vai com sinal.
        const variacaoNoPdf = (v: number | null) =>
            v === null ? '–' : `${v >= 0 ? '+' : '-'}${formatoPercentual.format(Math.abs(v))}%`;
        const head = [
            t('common.descricao'),
            ...colunasDosMeses.flatMap((i) => [translateMonth(meses[i]), ...(compararMesAnterior ? ['Var. %'] : [])]),
            ...(mostrarTotal ? ['Total'] : []),
        ];
        const textoDoValor = (valor: number, entradas: number) =>
            analiseHorizontal ? `${formatCurrency(valor)}\n${formatarPercentual(percentualDaReceita(valor, entradas))}` : formatCurrency(valor);

        const exportadas = linhasParaExportar();
        const body = exportadas.map((linha) => [
            recuo(linha) + linha.nome,
            ...colunasDosMeses.flatMap((i) => [
                textoDoValor(linha.valores[i] ?? 0, entradasDoMes(i)),
                ...(compararMesAnterior ? [variacaoNoPdf(variacaoDaLinha(linha, i))] : []),
            ]),
            ...(mostrarTotal ? [textoDoValor(soma(linha.valores), entradasNoAno)] : []),
        ]);

        const colunas = head.length;
        autoTable(doc, {
            startY: 32,
            // Topo e rodape reservados: o cabecalho da tabela se repete em cada pagina
            // e as linhas nunca ficam embaixo da faixa do rodape.
            margin: { top: 14, bottom: 18 },
            head: [head],
            body,
            styles: { fontSize: colunas > 20 ? 5.5 : colunas > 12 ? 6.5 : 8, cellPadding: 1.5, halign: 'right' },
            columnStyles: { 0: { halign: 'left', cellWidth: colunas > 12 ? 34 : 50 } },
            headStyles: { fillColor: [1, 95, 89], textColor: 255, fontStyle: 'bold', halign: 'right' },
            didParseCell: (data) => {
                if (data.section !== 'body') return;
                const linha = exportadas[data.row.index];
                if (linha.nivel === 'grupo') {
                    data.cell.styles.fillColor = linha.lado === 'entrada' ? [21, 128, 61] : [185, 28, 28];
                    data.cell.styles.textColor = 255;
                    data.cell.styles.fontStyle = 'bold';
                } else if (linha.nivel === 'categoria' || linha.nivel === 'saldo') {
                    data.cell.styles.fillColor = [243, 244, 246];
                    data.cell.styles.fontStyle = 'bold';
                }
            },
        });

        const pageCount = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFillColor(1, 95, 89);
            doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text('Financeiro Diário - Sistema de Gestão Financeira', 14, pageHeight - 5);
            doc.text(`Página ${i} de ${pageCount}`, pageWidth - 32, pageHeight - 5);
        }
        doc.save(`${nomeDoArquivo}.pdf`);
    };

    const mesesComMovimento = useMemo(
        () => (fluxoData ? mesesVisiveis(fluxoData, { periodo: 'ano', mostrarZerados: false }) : []),
        [fluxoData],
    );

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title={t('cashflow.titulo')} subtitle={t('cashflow.subtituloTabela')} />
                <div className={styles.content}>
                    <div className={styles.pageHeader}>
                        <div className={styles.periodoExibido}>
                            <span className={styles.controlLabel}>{t('cashflow.periodoExibido')}</span>
                            <SeletorDePeriodo
                                ano={selectedYear}
                                meses={meses.map(translateMonth)}
                                mesesComMovimento={mesesComMovimento}
                                value={periodo}
                                onChange={setPeriodo}
                                rotuloAnoInteiro={t('cashflow.anoInteiro')}
                            />
                        </div>
                        <div className={styles.controls}>
                            <Interruptor
                                ligado={compararMesAnterior}
                                onChange={setCompararMesAnterior}
                                rotulo={t('cashflow.compararMesAnterior')}
                            />
                            {periodo === 'ano' && (
                                <Interruptor
                                    ligado={mostrarZerados}
                                    onChange={setMostrarZerados}
                                    rotulo={t('cashflow.mostrarMesesZerados')}
                                />
                            )}
                            <Interruptor
                                ligado={analiseHorizontal}
                                onChange={setAnaliseHorizontal}
                                rotulo={t('cashflow.analiseHorizontal')}
                            />
                            <button className={styles.exportBtn} onClick={handleExportExcel} disabled={!fluxoData || isLoading}>
                                <FiFileText size={14} /> {t('cashflow.exportarExcel')}
                            </button>
                            <button className={styles.exportBtn} onClick={handleExportPDF} disabled={!fluxoData || isLoading}>
                                <FiFileText size={14} /> {t('cashflow.exportarPdf')}
                            </button>
                            <div className={styles.periodSelector}>
                                <YearPicker
                                    years={availableYears}
                                    selectedYear={selectedYear}
                                    onChange={setSelectedYear}
                                    label={t('common.ano')}
                                />
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#6b7280' }}>
                            {t('common.carregando')}
                        </div>
                    ) : null}

                    {!isLoading && (canScrollLeft || canScrollRight) && (
                        <div className={styles.scrollHint}>
                            <span>{t('cashflow.scrollHint')}</span>
                            <FiArrowRight className={styles.scrollHintIcon} size={14} />
                        </div>
                    )}

                    <div className={styles.dreTableOuter} style={{ display: isLoading ? 'none' : undefined }}>
                        <div className={styles.dreTableWrapper} ref={tableWrapperRef}>
                            {isMobile && periodo === 'ano' && meses.length > 0 && (
                            <div className={styles.mobileMonthNav}>
                                <button
                                    className={styles.mobileMonthNavBtn}
                                    onClick={() => setMobileMonthIndex(i => Math.max(0, i - 1))}
                                    disabled={mobileMonthIndex === 0}
                                    aria-label="Mês anterior"
                                >
                                    <FiChevronLeft size={16} />
                                </button>
                                <span className={styles.mobileMonthLabel}>{translateMonth(meses[mobileMonthIndex])}</span>
                                <button
                                    className={styles.mobileMonthNavBtn}
                                    onClick={() => setMobileMonthIndex(i => Math.min(meses.length - 1, i + 1))}
                                    disabled={mobileMonthIndex === meses.length - 1}
                                    aria-label="Próximo mês"
                                >
                                    <FiChevronRight size={16} />
                                </button>
                            </div>
                        )}
                        <table className={styles.dreTable}>
                            <thead>
                                <tr>
                                    <th ref={stickyColRef} className={styles.dreDescCol}>{t('common.descricao')}</th>
                                    {visibleMonthIndexes.map(i => (
                                        <Fragment key={i}>
                                            <th className={styles.dreValueCol}>{translateMonth(meses[i])}</th>
                                            {compararMesAnterior && <th className={`${styles.dreValueCol} ${styles.deltaCol}`}>Δ%</th>}
                                        </Fragment>
                                    ))}
                                    {mostrarTotal && (
                                        <th className={`${styles.dreValueCol} ${styles.dreValueColTotal}`}>Total</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {linhas.map((linha) => {
                                    const expansivel = linha.nivel === 'categoria';
                                    const aberta = expandedCategories.has(linha.key);
                                    const total = soma(linha.valores);
                                    return (
                                        <tr
                                            key={linha.key}
                                            className={classeDaLinha(linha)}
                                            onClick={expansivel ? () => toggleCategory(linha.key) : undefined}
                                        >
                                            <td className={linha.nivel === 'subcategoria' ? styles.dreSubCell : styles.dreCategoryCell}>
                                                {expansivel && (aberta ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />)}
                                                {linha.nivel === 'grupo' || linha.nivel === 'saldo'
                                                    ? <strong>{linha.nome}</strong>
                                                    : linha.nivel === 'categoria'
                                                        ? <span>{renderNome(linha.nome.toUpperCase())}</span>
                                                        : renderNome(linha.nome)}
                                            </td>
                                            {visibleMonthIndexes.map(i => (
                                                <Fragment key={i}>
                                                    {celulaDeValor(linha, linha.valores[i] ?? 0, entradasDoMes(i), `v${i}`)}
                                                    {compararMesAnterior && celulaDeVariacao(linha, i)}
                                                </Fragment>
                                            ))}
                                            {mostrarTotal && celulaDeValor(linha, total, entradasNoAno, 'total', true)}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                        <div
                            className={`${styles.edgeFade} ${styles.edgeFadeLeft} ${canScrollLeft ? styles.edgeFadeVisible : ''}`}
                            style={{ left: stickyColWidth }}
                        />
                        <div className={`${styles.edgeFade} ${styles.edgeFadeRight} ${canScrollRight ? styles.edgeFadeVisible : ''}`} />
                    </div>

                    {!isLoading && fluxoData && (
                        <div className={styles.notas}>
                            {periodo === 'ano' && !mostrarZerados && colunasDosMeses.length === 0 && <p>{t('cashflow.semMovimento')}</p>}
                            {mesesOcultos > 0 && colunasDosMeses.length > 0 && <p>{t('cashflow.notaMesesOcultos', { n: String(mesesOcultos) })}</p>}
                            {analiseHorizontal && <p>{t('cashflow.notaAnaliseHorizontal')}</p>}
                            {compararMesAnterior && <p>{t('cashflow.notaComparacao')}</p>}
                        </div>
                    )}
                </div>
            </main>

            {nomeModal && (
                <div className={styles.modalOverlay} onClick={() => setNomeModal(null)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <p className={styles.modalText}>{nomeModal}</p>
                        <button className={styles.modalClose} onClick={() => setNomeModal(null)}>{t('common.fechar')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};
